import React from 'react';
import CargomaticCard from 'components/v2/card';
import ChevronUp from 'components/v2/icons/ChevronUpIcon';
import ChevronDown from 'components/v2/icons/ChevronDownIcon';
import EmailIcon from 'components/v2/icons/EmailIcon';
import { headersByFieldNames } from 'components/modules/accounting/payouts/mappings';
import AccountingActionCreators from 'actions/AccountingActionCreators';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import {
  PayoutNumberCell,
  DateCell,
  BooleanCell,
  PayoutStatusCell,
  RawCell,
  MoneyCell,
  ActionsCell,
  PayoutCheckBoxCell,
} from './renderer';
import Checkbox from '../../../v2/forms/Checkbox';
import SessionStore from '../../../../stores/SessionStore';
import SendRateCardEmailModal from 'components/modules/user/rates/SendRateCardEmailModal';

const ACTIONS = {
  APPROVE: 'approve',
  REVERSE_APPROVAL: 'reverse approval',
  VOID: 'void',
  REVERSE_VOID: 'reverse void',
  BULK_STRIPE_PAYOUT: 'bulk stripe payout',
};

const STATUS = {
  APPROVED_INTERNALLY: 'approved_internally',
  PAYMENT_INITIALIZED: 'payment_initialized',
  PAID: 'paid',
  VOID: 'void',
};

const getDisableActionsState = (action, status) => {
  switch (action) {
    case ACTIONS.APPROVE:
      return [STATUS.PAYMENT_INITIALIZED, STATUS.PAID, STATUS.VOID].includes(
        status,
      );
    case ACTIONS.VOID:
      return [
        STATUS.APPROVED_INTERNALLY,
        STATUS.PAYMENT_INITIALIZED,
        STATUS.PAID,
      ].includes(status);
    case ACTIONS.BULK_STRIPE_PAYOUT:
      return [STATUS.APPROVED_INTERNALLY].includes(status);
    default:
      return false;
  }
};

class PayoutList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      sort: {
        key: props.sort.key,
        order: props.sort.order,
        showConfirmModal: false,
        confirmAction: '',
        currentPayout: null,
      },
      selectedPayouts: [],
      isFinanceUser: SessionStore.isFinanceAdmin(),
      modal: {
        showPayoutReportEmailModal: false,
      },
    };
  }

  getPayoutReportCardEmailRecipientList() {
    const loggedInUserEmail = SessionStore.getUser()?.email || '';
    return [loggedInUserEmail].filter(Boolean).join(', ').trim();
  }

  onHeaderClick(key) {
    const state = Object.assign({}, this.state);
    if (key === this.state.sort.key) {
      state.sort.order = state.sort.order === 'asc' ? 'desc' : 'asc';
    } else {
      state.sort = {
        key,
        order: 'asc',
      };
    }
    this.props.onSortChange(state.sort);
    this.setState(state);
  }

  onApproval = () => {
    const {
      currentPayout: { _id, status },
    } = this.state;

    if (status === STATUS.APPROVED_INTERNALLY) {
      return this.setState({ showConfirmModal: false }, () =>
        AccountingActionCreators.reversePayoutApproval(_id),
      );
    }
    return this.setState({ showConfirmModal: false }, () =>
      AccountingActionCreators.approvePayout(_id),
    );
  };

  onVoid = () => {
    const {
      currentPayout: { _id, status },
    } = this.state;

    if (status === STATUS.VOID) {
      return this.setState({ showConfirmModal: false }, () =>
        AccountingActionCreators.reversePayoutVoid(_id),
      );
    }
    return this.setState({ showConfirmModal: false }, () =>
      AccountingActionCreators.voidPayout(_id),
    );
  };

  onCancelClick = () => {
    this.setState({
      showConfirmModal: false,
      confirmAction: '',
      currentPayout: null,
    });
  };

  onConfirmClick = () => {
    switch (this.state.confirmAction) {
      case ACTIONS.APPROVE:
      case ACTIONS.REVERSE_APPROVAL:
        return this.onApproval();
      case ACTIONS.VOID:
      case ACTIONS.REVERSE_VOID:
        return this.onVoid();
      default:
        break;
    }
  };

  renderConfirmModal(action, data) {
    let confirmAction = '';

    switch (action) {
      case ACTIONS.APPROVE:
        if (data.status === STATUS.APPROVED_INTERNALLY) {
          confirmAction = ACTIONS.REVERSE_APPROVAL;
        } else {
          confirmAction = ACTIONS.APPROVE;
        }
        break;
      case ACTIONS.VOID:
        if (data.status === STATUS.VOID) {
          confirmAction = ACTIONS.REVERSE_VOID;
        } else {
          confirmAction = ACTIONS.VOID;
        }
        break;
      default:
        confirmAction = action;
        break;
    }

    this.setState({
      showConfirmModal: true,
      confirmAction,
      currentPayout: data,
    });
  }

  getSelectables = () => {
    return this.props.payouts.filter(
      (payout) =>
        getDisableActionsState(ACTIONS.BULK_STRIPE_PAYOUT, payout.status) &&
        payout.auto_pay_enabled,
    );
  };

  isAllItemsChecked = () => {
    const selectables = this.getSelectables();
    return (
      selectables.length > 0 &&
      this.state.selectedPayouts.length === selectables.length
    );
  };

  handleToggleCheckAll = () => {
    const selectables = this.getSelectables();
    if (this.state.selectedPayouts.length === selectables.length) {
      this.setState({ selectedPayouts: [] });
    } else {
      this.setState({
        selectedPayouts: selectables.map((payout) => payout._id),
      });
    }
  };

  handleToggleCheck = (payoutId) => {
    this.setState((prev) => {
      let selectedPayouts = [...prev.selectedPayouts];
      const index = selectedPayouts.indexOf(payoutId);
      if (index === -1) {
        selectedPayouts.push(payoutId);
      } else {
        selectedPayouts.splice(index, 1);
      }
      return { selectedPayouts };
    });
  };

  disableLineItem = (payout) => {
    const { status, auto_pay_enabled } = payout;
    return (
      !getDisableActionsState(ACTIONS.BULK_STRIPE_PAYOUT, status) ||
      !this.state.isFinanceUser ||
      !auto_pay_enabled
    );
  };

  renderHeaders() {
    const { t } = this.props;
    return (
      <tr className="">
        {Object.keys(headersByFieldNames).map((key) => {
          const icon =
            this.state.sort.order === 'asc' ? <ChevronDown /> : <ChevronUp />;

          const thClassNames = [''];
          let sortArrow = '';
          if (key === this.state.sort.key) {
            thClassNames.push('current-sort');
            sortArrow = <div className="cursor-pointer">{icon}</div>;
          }

          if (key === 'select_row') {
            return (
              <Checkbox
                className="cursor-pointer p-6"
                id={key}
                checked={this.isAllItemsChecked()}
                onChange={this.handleToggleCheckAll}
                disabled={!this.state.isFinanceUser}
              />
            );
          }

          return (
            <th
              data-key={key}
              key={key}
              onClick={this.onHeaderClick.bind(this, key)}
              className={thClassNames.join(' ')}
            >
              <div className="flex items-center min-h-6 space-x-1">
                <span>{t(headersByFieldNames[key])}</span>
                {sortArrow}
              </div>
            </th>
          );
        })}
      </tr>
    );
  }

  renderRow(payout) {
    const { t } = this.props;
    const {
      _id,
      date_created,
      due_date,
      carrier_name,
      total,
      type,
      status,
      auto_pay_enabled,
      number,
      authorized,
      payment_method,
      payment_terms,
      currency,
    } = payout;
    return (
      <tr data-id={_id} key={_id}>
        <PayoutCheckBoxCell
          payoutId={_id}
          checked={this.state.selectedPayouts.includes(_id)}
          handleToggleCheck={this.handleToggleCheck}
          disabled={this.disableLineItem(payout)}
        />
        <PayoutNumberCell data={payout} />
        <DateCell data={date_created} />
        <DateCell data={due_date} />
        <RawCell data={carrier_name} />
        <MoneyCell data={total} currency={currency} />
        <RawCell data={t(sluggify(type))} />
        <PayoutStatusCell data={status} />
        <RawCell data={t(sluggify(payment_method[0]))} />
        <RawCell data={payment_terms} />
        <BooleanCell data={auto_pay_enabled} />
        <BooleanCell
          data={authorized && authorized.length ? authorized[0] : false}
        />
        <ActionsCell
          approveAction={() =>
            this.renderConfirmModal(ACTIONS.APPROVE, { _id, number, status })
          }
          declineAction={() =>
            this.renderConfirmModal(ACTIONS.VOID, { _id, number, status })
          }
          disableApprove={getDisableActionsState(ACTIONS.APPROVE, status)}
          disableDecline={getDisableActionsState(ACTIONS.VOID, status)}
        />
      </tr>
    );
  }

  updatePayoutReportEmailModalState = (modalVisibility) => {
    this.setState((prevState) => ({
      modal: {
        ...prevState.modal,
        showPayoutReportEmailModal: modalVisibility,
      },
    }));
  };

  handleEmailSubmit = (recipients) => {
    this.props.handleSendPayoutsReportToEmail(recipients);
  };

  renderTitleBar() {
    const { t } = this.props;
    return (
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          {t('PAYOUT', { count: 2 })}
        </h3>
        {SessionStore.isAdmin() && (
          <button
            title={t('SEND_CSV_TO_EMAIL')}
            className="flex item-center button button-hollow-green"
            onClick={() => this.updatePayoutReportEmailModalState(true)}
          >
            {t('SEND CSV TO EMAIL')}
            <EmailIcon />
          </button>
        )}
      </div>
    );
  }

  render() {
    const rows = this.props.payouts.map((payout) => this.renderRow(payout));
    const { t } = this.props;
    return (
      <>
        <CargomaticCard header={() => this.renderTitleBar()}>
          <div className="-mb-4 overflow-x-auto card--no-x-padding card--no-top-padding sm:-mb-4">
            <table className="default-table">
              <thead>{this.renderHeaders()}</thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
          {this.state.showConfirmModal && (
            <ConfirmModal
              show={this.state.showConfirmModal}
              title={t('PAYOUT_STATUS_CONFIRMATION_WITH_VALUE', {
                confirmStatus: t(sluggify(this.state.confirmAction)),
                confirmAction: [
                  ACTIONS.REVERSE_APPROVAL,
                  ACTIONS.REVERSE_VOID,
                ].includes(this.state.confirmAction)
                  ? t('OF')
                  : '',
                payoutNumber: this.state.currentPayout.number,
              })}
              onConfirm={this.onConfirmClick}
              onHide={this.onCancelClick}
              message={t('ARE_YOU_SURE')}
              hideButtonText={t('CANCEL')}
              affirmative={t('OK')}
            />
          )}
        </CargomaticCard>
        <SendRateCardEmailModal
          modalTitle="Email Payout Report"
          show={this.state.modal.showPayoutReportEmailModal}
          onHide={() => this.updatePayoutReportEmailModalState(false)}
          onSend={this.handleEmailSubmit}
          defaultRecipient={this.getPayoutReportCardEmailRecipientList()}
        />
      </>
    );
  }
}

export default withTranslation()(PayoutList);
